import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Campaign, Collab, FoodfluenceLocation, usesOpenHoursScheduling, AvailableScheduleSlotDto } from '@foodfluence/shared';
import { collabApi } from 'services/api/collab';
import { getCollabAvailableTimes } from 'utils/collabUtils';
import { getAvailableDatesAndTimes } from 'features/collabs/scheduling/utils/timeOptionsUtils';

export type SchedulingSlot = AvailableScheduleSlotDto;

export interface SchedulingAvailabilityState {
  timeOptions: Map<string, SchedulingSlot[]>;
  hasBookableSlots: boolean;
  hasMoreSlots: boolean;
  expanded: boolean;
  showMore: () => void;
  loading: boolean;
  error: string | null;
  usesBackendSlots: boolean;
}

const slotsRecordToMap = (
  slots: Record<string, SchedulingSlot[]>
): Map<string, SchedulingSlot[]> => {
  return new Map(Object.entries(slots));
};

export const useSchedulingAvailability = (
  campaign: Campaign | null | undefined,
  collab: Collab | null | undefined,
  locations: FoodfluenceLocation[],
  locationId?: string,
  enabled: boolean = true
): SchedulingAvailabilityState => {
  const [timeOptions, setTimeOptions] = useState<Map<string, SchedulingSlot[]>>(new Map());
  const [hasBookableSlots, setHasBookableSlots] = useState(true);
  const [hasMoreSlots, setHasMoreSlots] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Monotonic request id: the parent re-renders often and fires overlapping fetches, so we
  // only apply the newest response — otherwise a late curated fetch can clobber an expanded one.
  const requestSeqRef = useRef(0);
  // The recommended slot is recomputed per response, so it jumps when "show more" reveals an
  // earlier day. Pin the first recommendation and keep it flagged across expand, so the
  // original pick stays recommended alongside the expanded view's own.
  const stickyRecommendedRef = useRef<{ dateKey: string; time: string } | null>(null);

  const { hasSpecificTimeIntervals } = useMemo(() => {
    if (!collab || !campaign) {
      return { availableTimes: {}, hasSpecificTimeIntervals: false };
    }
    return getCollabAvailableTimes(collab, campaign);
  }, [collab, campaign]);

  const usesBackendSlots = useMemo(() => {
    if (!campaign || !collab) return false;
    return usesOpenHoursScheduling(campaign, collab);
  }, [campaign, collab]);

  const fetchAvailability = useCallback(async () => {
    if (!enabled || !campaign || !collab) return;

    const seq = ++requestSeqRef.current;
    const isStale = () => seq !== requestSeqRef.current;

    setLoading(true);
    setError(null);

    try {
      if (usesBackendSlots) {
        const response = await collabApi.getAvailableScheduleSlots(
          campaign.id,
          collab.id,
          locationId,
          undefined,
          expanded
        );
        if (isStale()) return;
        const map = slotsRecordToMap(response.slots);
        // Capture the first recommendation; re-apply it on every response so it survives
        // "show more" (the response objects are fresh, safe to mutate).
        if (!stickyRecommendedRef.current) {
          for (const [dk, arr] of map) {
            const rec = arr.find((s) => s.recommended);
            if (rec) {
              stickyRecommendedRef.current = { dateKey: dk, time: rec.time };
              break;
            }
          }
        }
        const sticky = stickyRecommendedRef.current;
        if (sticky) {
          const stickySlot = map.get(sticky.dateKey)?.find((s) => s.time === sticky.time);
          if (stickySlot) stickySlot.recommended = true;
        }
        setTimeOptions(map);
        setHasBookableSlots(response.hasBookableSlots);
        setHasMoreSlots(response.hasMoreSlots ?? false);
        return;
      }

      const { availableTimes } = getCollabAvailableTimes(collab, campaign);
      const clientSlots = getAvailableDatesAndTimes(
        hasSpecificTimeIntervals,
        campaign,
        collab,
        locations,
        availableTimes,
        locationId
      );
      if (isStale()) return;
      setTimeOptions(clientSlots);
      setHasBookableSlots(clientSlots.size > 0);
      setHasMoreSlots(false);
    } catch (err) {
      if (isStale()) return;
      console.error('Failed to load scheduling availability', err);
      setError('Unable to load available times');
      setTimeOptions(new Map());
      setHasBookableSlots(false);
      setHasMoreSlots(false);
    } finally {
      if (!isStale()) setLoading(false);
    }
    // Depend on stable ids, not the campaign/collab objects: those get new references on
    // every context update, which would otherwise recreate this callback and storm refetches
    // — starving the show-more (expanded) response, which never gets to apply.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    campaign?.id,
    collab?.id,
    usesBackendSlots,
    hasSpecificTimeIntervals,
    locations,
    locationId,
    expanded,
  ]);

  // Reset the pinned recommendation when the context changes (new collab/campaign/location) —
  // but not on expand, so "show more" keeps the original pick.
  useEffect(() => {
    stickyRecommendedRef.current = null;
  }, [campaign?.id, collab?.id, locationId]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  return {
    timeOptions,
    hasBookableSlots,
    hasMoreSlots,
    expanded,
    showMore: () => setExpanded(true),
    loading,
    error,
    usesBackendSlots,
  };
};

export const isSchedulingCapacityExhausted = (
  campaign: Campaign | null | undefined,
  collab: Collab | null | undefined,
  hasBookableSlots: boolean,
  usesBackendSlots: boolean
): boolean => {
  if (!campaign || !collab || !usesBackendSlots) return false;
  // Backend applies capacity using full campaign data from DB.
  return !hasBookableSlots;
};

export const fetchSchedulingCapacityStatus = async (
  campaign: Campaign,
  collab: Collab,
  locationId?: string
): Promise<{ schedulingFullyBooked: boolean }> => {
  if (!usesOpenHoursScheduling(campaign, collab)) {
    return { schedulingFullyBooked: false };
  }

  try {
    const response = await collabApi.getAvailableScheduleSlots(
      campaign.id,
      collab.id,
      locationId
    );
    return {
      schedulingFullyBooked: !response.hasBookableSlots,
    };
  } catch (error) {
    console.error('Failed to fetch scheduling capacity status', error);
    return { schedulingFullyBooked: false };
  }
};
